"use client";

import type {VariantProps} from "@gluestack-ui/nativewind-utils";
import {tva} from "@gluestack-ui/nativewind-utils/tva";
import {withStyleContext} from "@gluestack-ui/nativewind-utils/withStyleContext";
import {createTooltip} from "@gluestack-ui/tooltip";
import {AnimatePresence, Motion} from "@legendapp/motion";
import {cssInterop} from "nativewind";
import React, {Fragment, useCallback, useImperativeHandle, useRef} from "react";
import type {GestureResponderEvent, NativeSyntheticEvent, TargetedEvent} from "react-native";
import {View} from "react-native";

import {isReactElement, useDisclose, useTimeout} from "../../../react";
import {Box} from "../box";
import {Pressable} from "../pressable";
import {Text} from "../text";

const Root = withStyleContext(View);
const Content = Motion.View as typeof Root;
export const UITooltip = createTooltip({
    Root,
    Content,
    Text,
    AnimatePresence
});

cssInterop(Motion.View as typeof Content, {className: "style"});

const tooltipStyle = tva({
    base: "w-full h-full web:pointer-events-none"
});

const tooltipContentStyle = tva({
    base: "max-w-[320px] rounded-md bg-gray-800/90 web:pointer-events-auto opacity-90"
});

const tooltipTextStyle = tva({
    base: "font-normal tracking-normal text-xs text-typography-50 dark:text-typography-500",
    variants: {
        isTruncated: {
            true: {
                props: "line-clamp-1 truncate"
            }
        },
        bold: {
            true: "font-bold"
        },
        underline: {
            true: "underline"
        },
        strikeThrough: {
            true: "line-through"
        },
        size: {
            "2xs": "text-2xs",
            xs: "text-xs",
            sm: "text-sm",
            md: "text-base",
            lg: "text-lg",
            xl: "text-xl",
            "2xl": "text-2xl",
            "3xl": "text-3xl",
            "4xl": "text-4xl",
            "5xl": "text-5xl",
            "6xl": "text-6xl"
        },
        sub: {
            true: "text-xs"
        },
        italic: {
            true: "italic"
        },
        highlight: {
            true: "bg-yellow-500"
        }
    }
});

type ITooltipUIProps = React.ComponentProps<typeof UITooltip> &
    VariantProps<typeof tooltipStyle> & {className?: string};
type ITooltipContentProps = React.ComponentProps<typeof UITooltip.Content> &
    VariantProps<typeof tooltipContentStyle> & {className?: string};
type ITooltipTextProps = React.ComponentProps<typeof UITooltip.Text> &
    VariantProps<typeof tooltipTextStyle> & {className?: string};

const TooltipUI = React.forwardRef<React.ComponentRef<typeof UITooltip>, ITooltipUIProps>(
    ({className, ...props}, ref) => {
        return <UITooltip ref={ref} className={tooltipStyle({class: className})} {...props} />;
    }
);

const TooltipContent = React.forwardRef<
    React.ComponentRef<typeof UITooltip.Content>,
    ITooltipContentProps & {className?: string}
>(({className, ...props}, ref) => {
    return (
        <UITooltip.Content
            ref={ref}
            {...props}
            className={tooltipContentStyle({
                class: className
            })}
            pointerEvents="auto"
        />
    );
});

const TooltipText = React.forwardRef<
    React.ComponentRef<typeof UITooltip.Text>,
    ITooltipTextProps & {className?: string}
>(({size, className, ...props}, ref) => {
    return (
        <UITooltip.Text
            ref={ref}
            className={tooltipTextStyle({
                size,
                class: className
            })}
            {...props}
        />
    );
});

type ITooltipProps = {
    label: React.ReactNode;
    children?: React.ReactNode;
    className?: string;
    wrapperClassName?: string;
    contentClassName?: string;
    placement?: ITooltipUIProps["placement"];
    interaction?: boolean;
    isOpenDefault?: boolean;
    onPress?: () => void;
};

export type TooltipRef = {
    openTooltip: () => void;
    closeTooltip: () => void;
};

const Tooltip = React.forwardRef<TooltipRef, ITooltipProps>(
    (
        {
            className,
            wrapperClassName,
            contentClassName,
            label,
            children,
            placement = "top",
            interaction,
            isOpenDefault,
            onPress: onPressProp
        },
        ref
    ) => {
        const {isOpen, onOpen, onClose} = useDisclose(isOpenDefault);
        const contentFocused = useRef<boolean>(false);

        const delayHideTooltip = useTimeout((callback?: () => void) => {
            if (!contentFocused.current) {
                onClose();
                callback?.();
            }
        }, 200);

        useImperativeHandle(ref, () => ({openTooltip: onOpen, closeTooltip: onClose}));

        const handleFocusContent = useCallback(() => {
            contentFocused.current = true;
        }, []);

        const handleBlurContent = useCallback(() => {
            contentFocused.current = false;
            onClose();
        }, [onClose]);

        const handleHoverInContent = useCallback(() => {
            contentFocused.current = true;
        }, []);

        const handleHoverOutContent = useCallback(() => {
            contentFocused.current = false;
            onClose();
        }, [onClose]);

        const trigger: ITooltipUIProps["trigger"] = useCallback(
            triggerProps => {
                const {onBlur, onFocus, onMouseLeave, onMouseEnter, onPress, ...rest} =
                    triggerProps;

                const handleBlur = (e: NativeSyntheticEvent<TargetedEvent>) => {
                    if (!interaction) {
                        onBlur?.(e);
                    }
                    delayHideTooltip(() => onBlur?.(e));
                };

                const handleFocus = (e: NativeSyntheticEvent<TargetedEvent>) => {
                    onFocus?.(e);
                    if (interaction) {
                        onOpen();
                    }
                };

                const handlePress = (e: GestureResponderEvent) => {
                    if (!interaction) {
                        onPressProp?.();
                        onPress?.(e);
                    }
                };

                const handleMouseLeave = () => {
                    if (!interaction) {
                        onMouseLeave?.();
                    }
                    delayHideTooltip(onMouseLeave);
                };

                return (
                    <Pressable
                        {...rest}
                        className={wrapperClassName}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        onMouseEnter={process.env.IS_WEB ? onMouseEnter : undefined}
                        onMouseLeave={process.env.IS_WEB ? handleMouseLeave : undefined}
                        onPress={handlePress}
                        onPressIn={process.env.IS_WEB ? undefined : onOpen}
                        onPressOut={process.env.IS_WEB ? undefined : onClose}
                    >
                        {children}
                    </Pressable>
                );
            },
            [
                children,
                delayHideTooltip,
                interaction,
                wrapperClassName,
                onClose,
                onOpen,
                onPressProp
            ]
        );

        return (
            <TooltipUI
                className={className}
                isOpen={isOpen}
                placement={placement}
                trigger={trigger}
                onClose={onClose}
                onOpen={onOpen}
            >
                <TooltipContent className={contentClassName}>
                    {process.env.IS_WEB ? (
                        <div
                            className="py-2 px-3"
                            onBlur={handleBlurContent}
                            onFocus={handleFocusContent}
                            onMouseEnter={handleHoverInContent}
                            onMouseLeave={handleHoverOutContent}
                        >
                            {typeof label === "string" || isReactElement(label, Fragment) ? (
                                <TooltipText testID="tooltip-label">{label}</TooltipText>
                            ) : (
                                label
                            )}
                        </div>
                    ) : (
                        <Box className="py-2 px-3">
                            {typeof label === "string" ? <TooltipText>{label}</TooltipText> : label}
                        </Box>
                    )}
                </TooltipContent>
            </TooltipUI>
        );
    }
);

TooltipUI.displayName = "TooltipUI";
TooltipContent.displayName = "TooltipContent";
TooltipText.displayName = "TooltipText";
Tooltip.displayName = "Tooltip";

export {Tooltip, TooltipUI, TooltipContent, TooltipText};
